/**
 * English catalog — the source of truth for message keys.
 *
 * Keys are flat, dotted and grouped by the surface they belong to. Every other
 * locale is typed as `Messages` (see `../types.js`), so adding a key here makes
 * TypeScript fail until every language defines it.
 *
 * Conventions:
 *  - Placeholders are `{name}` and are substituted by `t()` / `tNode()`.
 *  - A plural message is two keys, `<base>.one` and `<base>.other`, selected by
 *    `Intl.PluralRules`; `tPlural()` only accepts a base that has `.other`.
 *  - Keys under a data vocabulary (`status.*`, `role.*`, `provenance.*`, …)
 *    mirror contract enum values and are looked up with `tOr()`, which falls
 *    back to the raw value when the engine adds a new one.
 *
 * Voice — Apple Style Guide + HIG Writing:
 *  - Title-style capitalization for anything you act on or that titles a surface:
 *    buttons, menu items, navigation, page, card and dialog titles, empty-state
 *    titles, column headers, key-value and field labels, accessibility names.
 *    Capitalize the first and last word and everything between except articles,
 *    coordinating conjunctions, and prepositions of three letters or fewer.
 *  - Sentence-style capitalization for anything that reads as prose: descriptions,
 *    hints, placeholders, toasts, validation and error messages, phrase-length
 *    radio labels. Full sentences take a final period; fragments don’t.
 *  - Data vocabulary stays lowercase — those are values the engine reports, and
 *    they appear inline in sentences and badges, not as labels.
 *  - Typographic punctuation only: ’ for apostrophes, “ ” for quotes, … for ellipsis.
 *  - Second person, active voice, present tense. Serial comma. Contractions are
 *    welcome. No “we”, no “please”, no “simply”, no “you can”, no exclamation points.
 *  - One short sentence per description. If the title is enough, stop. Don’t
 *    lecture, sell, or recap product philosophy. No invariant IDs, no roadmap.
 *  - Empty states name the absence (“No Decisions”), not the journey (“No … Yet”).
 *  - Placeholders are examples or short fragments, not questions.
 *  - Say what happened and what to do next rather than “failed”.
 *  - One term per concept, here and in every other catalog: decision, constraint,
 *    change, component, evidence, memory, workspace, agent, pull request, finding,
 *    drift, amendment, lineage. A constraint is never a “rule”: rules are the
 *    approval rules. The queue is Approvals, a path is mapped (not claimed) to a
 *    component, and connectors are explored (not synced).
 *  - Destructive confirmations repeat the verb and name the object (“Revoke Key”).
 *  - Never let silence read as approval: where memory knows nothing, the copy says so.
 */
export const en = {
  // ---------------------------------------------------------------- common
  "common.back": "Back",
  "common.cancel": "Cancel",
  "common.confirm": "Confirm",
  "common.close": "Close",
  "common.dismiss": "Dismiss",
  "common.copy": "Copy",
  "common.loading": "Loading…",
  "common.none": "—",
  "common.nothingToReview": "Nothing to review.",
  "common.unsavedChanges": "Unsaved changes",

  // ------------------------------------------------------------- languages
  "language.label": "Language",

  // ----------------------------------------------------------- appearance
  "appearance.label": "Appearance",
  "appearance.system": "System",
  "appearance.light": "Light",
  "appearance.dark": "Dark",

  // --------------------------------------------------------- error states
  "error.title": "Something Went Wrong",
  "error.notFound": "Not Found",
  "error.status": "Error {status}",
  "error.forbidden": "You don’t have access to this workspace.",
  "error.network": "Can’t Connect",
  "error.requestFailed": "Couldn’t Complete Request",
  "error.requestId": "Request {id}",
  "error.backToWorkspaces": "Back to Workspaces",

  // ------------------------------------------------------------ not found
  "notFound.workspace": "You’re not a member of this workspace, or it doesn’t exist.",
  "notFound.title": "Page Not Found",
  "notFound.hint": "This page doesn’t exist.",
  "notFound.action": "Back to Workspaces",

  // -------------------------------------------------------------- sign in
  "signIn.title": "Sign In",
  "signIn.subtitle": "Decision memory.",
  "signIn.github": "Continue with GitHub",
  "signIn.or": "or",
  "signIn.submit": "Sign In",
  "signIn.failed": "Couldn’t sign in. Check your email and password, then try again.",
  "signIn.noAccount": "No account? {link}",
  "signIn.signUpLink": "Sign up",

  // -------------------------------------------------------------- sign up
  "signUp.title": "Create Account",
  "signUp.subtitle": "Next, join or create a workspace.",
  "signUp.submit": "Sign Up",
  "signUp.failed": "Couldn’t create your account. Try again.",
  "signUp.passwordHint": "At least 10 characters.",
  "signUp.namePlaceholder": "Ada Lovelace",
  "signUp.haveAccount": "Already have an account? {link}",
  "signUp.signInLink": "Sign in",

  // ------------------------------------------------------------- identity
  "field.name": "Name",
  "field.email": "Email",
  "field.password": "Password",
  "field.emailPlaceholder": "you@company.com",

  // ----------------------------------------------------------- invitation
  "invitation.joining": "Joining workspace…",
  "invitation.errorTitle": "Couldn’t Accept Invitation",
  "invitation.invalid": "This invitation isn’t valid for your account.",
  "invitation.goToWorkspaces": "Back to Workspaces",

  // -------------------------------------------------------- new workspace
  "workspaceNew.title": "Create Workspace",
  "workspaceNew.subtitle": "One workspace per organization.",
  "workspaceNew.name": "Workspace Name",
  "workspaceNew.namePlaceholder": "Acme Engineering",
  "workspaceNew.slug": "URL Slug",
  "workspaceNew.slugPlaceholder": "acme",
  "workspaceNew.slugHint": "Lowercase letters, numbers, and hyphens. Used in /w/<slug>.",
  "workspaceNew.submit": "Create Workspace",
  "workspaceNew.slugTaken": "This URL slug is taken.",
  "workspaceNew.slugTakenHint": "Choose a different slug.",

  // ================================================================ Workspace setup (D-0022)
  "setup.title": "Connect Your Sources",
  "setup.subtitle": "Diotia finds the decisions your team has already made, for you to confirm.",
  "setup.connect": "Connect",
  "setup.connected": "Connected",
  "setup.upload": "Upload Files",
  "setup.upload.hint": "Markdown or plain text",
  "setup.upload.choose": "Choose Files",
  "setup.upload.clear": "Clear",
  "setup.upload.count.one": "{count} file",
  "setup.upload.count.other": "{count} files",
  "setup.upload.tooLarge": "{name} is larger than 500 KB and wasn’t added.",
  "setup.next": "Next",
  "setup.skip": "Skip",
  "setup.tab.manual": "Add Manually",
  "setup.tab.agent": "Use an Agent",
  "setup.agent.hint": "Copy this to Claude Code, Codex, or another coding agent.",
  "setup.agent.prompt": "Add the engineering decisions the {workspace} organization has already made to Diotia.\n\n1. Install Diotia’s MCP server: name diotia, HTTP transport, URL {url}\n   - Claude Code: claude mcp add --transport http diotia {url}\n   - Codex: codex mcp add diotia --url {url}\n2. Authorize it: connecting opens Diotia in the browser, where I choose the {workspace} workspace and allow access. In Codex, start with codex mcp login diotia; in Claude Code, authenticate from /mcp. If this session can’t use the diotia tools yet, tell me what to do next, and continue once it’s authorized.\n3. Find the {workspace} organization’s code repositories (for example with gh repo list for that organization, or the ones I’ve cloned locally). Read each one’s README, docs, ADRs, commit history, and pull requests, and find the engineering decisions already made: technology choices, architecture, service boundaries, public interfaces, data stores, and dependencies that are hard to reverse. Bug fixes, refactors, and dependency bumps don’t count.\n4. For each decision, check for duplicates with search_decisions, then propose it with propose_decision:\n   - title, outcome: what was decided\n   - rationale: only the reasons the source states; leave it empty rather than invent one\n   - scope: the repository or service it applies to\n   - evidence: a link to the source (file, commit, or pull request) and the exact excerpt\n   Only propose; never approve.\n5. When you’re done, tell me which repositories you read and how many decisions you proposed.",
  "setup.agent.copied": "Prompt copied",
  "setup.agent.copyFailed": "Clipboard unavailable. Select the prompt and copy it.",
  "setup.agent.done": "Done",
  "setup.readOnly": "Only owners and admins can set up this workspace.",
  "setup.failed": "Couldn’t Read Your Sources Last Time",
  "setup.error.no_pages": "No pages to read.",
  "setup.error.unauthorized": "Access was revoked. Connect again.",
  "setup.error.rate_limited": "Too many requests. Try again later.",
  "setup.error.timed_out": "Took too long. Try again.",
  "setup.error.model_not_configured": "This server has no model set up for exploring.",
  "setupGate.running": "Setting Up Your Workspace…",
  "setupGate.pending": "Set Up This Workspace First",
  "setupGate.add": "Connect Sources",

  // ---------------------------------------------------- workspace chrome
  "workspace.switcherAria": "Workspace: {name}",
  "workspace.switch": "Switch Workspace",
  "workspace.new": "New Workspace",
  "user.menuAria": "Account Menu",
  "user.signedIn": "Signed in",
  "user.signOut": "Sign Out",
  "nav.aria": "Workspace Navigation",
  // Every item is something you do. Component and Constraint are not here on
  // purpose: they are reached from a finding or a cascade.
  "nav.getStarted": "Get Started",
  "nav.inbox": "Inbox",
  "nav.reviews": "Approvals",
  "nav.drafts": "Drafts",
  "nav.myChanges": "My Changes",
  "nav.decisions": "Decisions",
  "nav.health": "Health",
  "nav.settings": "Settings",
  // ------------------------------------------------------------- decisions
  "decisions.title": "Decisions",
  "decisions.description": "Accepted decisions change only through an amendment or a successor.",
  "decisions.exportAll": "Export All",
  "decisions.import": "Import Markdown",
  "decisions.draft": "Draft Decision",
  "decisions.empty.title": "No Decisions",
  "decisions.empty.hint": "Draft a decision or import Markdown records.",
  "decisions.nextPage": "Next Page",
  "decisions.th.id": "ID",
  "decisions.th.title": "Title",
  "decisions.th.status": "Status",
  "decisions.th.scope": "Scope",
  "decisionNew.title": "Draft Decision",
  "decisionNew.successorTitle": "Draft Successor to {id}",
  "decisionNew.description": "Only a person can accept a decision.",
  "decisionNew.submit": "Save Draft",

  // -------------------------------------------------------- decision form
  "decisionForm.title": "Title",
  "decisionForm.titlePlaceholder": "Use PlanetScale PostgreSQL as the only relational store",
  "decisionForm.scope": "Scope",
  "decisionForm.scopePlaceholder": "repository, service, or component name",
  "decisionForm.scopeHint": "Leave blank for the whole workspace.",
  "decisionForm.context": "Context",
  "decisionForm.contextPlaceholder": "The situation that requires a decision",
  "decisionForm.problem": "Problem",
  "decisionForm.problemPlaceholder": "What must be decided",
  "decisionForm.outcome": "Decision",
  "decisionForm.outcomePlaceholder": "The choice",
  "decisionForm.rationale": "Rationale",
  "decisionForm.rationalePlaceholder": "Why this option",
  "decisionForm.supersedes": "Supersedes",
  "decisionForm.supersedesHint": "Replaced when this decision is accepted.",
  "decisionForm.dependsOn": "Depends On",

  // ------------------------------------------------------ decision detail
  "decision.approvalRecorded": "Your approval is recorded: {have} of {required}. It takes effect when the approval rules are met.",
  "decision.retirementRequested": "Retirement requested as {ref}. It takes effect when the approval rules are met.",
  "decision.reviewsLink": "Open Approvals",
  "decision.unknownAction": "Unknown Action",
  "decision.edit": "Edit",
  "decision.propose": "Propose",
  "decision.reject": "Reject",
  "decision.accept": "Accept",
  "decision.downloadMarkdown": "Download Markdown",
  "decision.draftSuccessor": "Draft Successor",
  "decision.superseded.title": "Superseded",
  "decision.superseded.body": "Kept for history. Replaced by {links}.",
  "decision.kv.scope": "Scope",
  "decision.kv.workspaceWide": "workspace-wide",
  "decision.kv.origin": "Origin",
  "decision.kv.revision": "Revision",
  "decision.kv.createdBy": "Created By",
  "decision.kv.approvedBy": "Approved By",
  "decision.kv.supersedes": "Supersedes",
  "decision.kv.supersededBy": "Superseded By",
  "decision.kv.dependsOn": "Depends On",
  "decision.section.context": "Context",
  "decision.section.problem": "Problem",
  "decision.section.outcome": "Decision",
  "decision.section.rationale": "Rationale",
  "decision.emptyBody": "No body. Add context, decision, and rationale in the draft.",
  "decision.alternatives": "Alternatives Considered",
  "decision.assumptions": "Assumptions",
  "decision.consequences": "Consequences",
  "decision.lineage.title": "Supersession Chain",
  "decision.lineage.earlier": "Earlier",
  "decision.lineage.later": "Later",
  "decision.constraints.title": "Constraints Introduced",
  "decision.constraints.th.id": "ID",
  "decision.constraints.th.statement": "Statement",
  "decision.constraints.th.status": "Status",
  "decision.constraints.keptBy": " (kept by {id})",
  "decision.constraints.replacedBy": " (replaced by {id})",
  "decision.deprecate.summary": "Deprecate Decision",
  "decision.deprecate.reason": "Reason",
  "decision.deprecate.reasonHint": "The decision stays on record, and its constraints stop applying.",
  "decision.deprecate.submit": "Deprecate",
  "decision.history.title": "History",
  "decision.history.description": "Each entry is a snapshot and is never changed.",
  "decision.history.actor": "{actor} ({kind})",
  "decision.history.policy": "policy {id}",
  "decision.history.human": "human",
  "decision.amendments.title": "Amendments",
  "decision.amendments.description": "Adds to an accepted decision without changing its outcome.",
  "decision.amendments.by": "by {actor}",
  "decision.amendments.addedAssumption": "+ assumption: {text}",
  "decision.amendments.accept": "Accept {ref}",
  "decision.amendments.reject": "Reject {ref}",
  "decision.amendments.propose": "Propose Amendment",
  "decision.amendments.summary": "Summary",
  "decision.amendments.clarification": "Clarification",
  "decision.amendments.newAssumptions": "New Assumptions",
  "decision.amendments.onePerLine": "One per line.",
  "decision.amendments.submit": "Propose Amendment",
  "decisionEdit.title": "Edit Decision",
  "decisionEdit.description": "Edits are kept in history. Accepted decisions change by amendment or successor.",
  "decisionEdit.submit": "Save",

  // ------------------------------------------------------ markdown import
  "import.title": "Import Markdown",
  "import.description": "Keeps each file’s decision_id, or assigns a new one. Only owners and admins can import.",
  "import.noFiles": "Choose at least one Markdown file.",
  "import.files": "Files",
  "import.filesHint": "ADR, RFD, or decision Markdown (Nygard, MADR, Diotia).",
  "import.filesAction": "Choose Files",
  "import.filesEmpty": "No files chosen. Drop files here.",
  "import.filesClear": "Clear",
  "import.filesCount.one": "{count} file",
  "import.filesCount.other": "{count} files",
  "import.repository": "Repository",
  "import.repositoryPlaceholder": "https://github.com/acme/api",
  "import.repositoryHint": "Optional. Source of the imported files.",
  "import.onConflict": "On Conflict",
  "import.onConflict.fail": "Report and skip",
  "import.onConflict.preferCloud": "Keep memory version",
  "import.onConflict.preferFile": "Use file (drafts and proposals)",
  "import.preview": "Preview",
  "import.apply": "Import",
  "import.previewTitle": "Import Preview",
  "import.resultTitle": "Import Result",
  "import.previewNote": "These actions will run on import.",
  "import.th.file": "File",
  "import.th.decision": "Decision",
  "import.th.action": "Action",
  "import.th.details": "Details",
  "import.conflictLine": "{field}: file “{file}” · memory “{cloud}”",

  // --------------------------------------------------------------- changes
  "changes.th.change": "Change",
  "changes.th.scope": "Scope",
  "changes.th.status": "Status",
  "change.reviewAgain": "Review Again",
  "change.reviewed.one": "Reviewed {revision}: {count} finding.",
  "change.reviewed.other": "Reviewed {revision}: {count} findings.",
  "change.dismissed": "Dismissed. Won’t appear again.",
  "change.exceptionRecorded": "Recorded as an accepted exception.",
  "change.confirmed": "Confirmed.",
  "change.kv.status": "Status",
  "change.kv.scope": "Scope",
  "change.kv.references": "References",
  "change.kv.components": "Components",
  "change.components.none": "none mapped",
  "change.component": "{name} ({files}, {provenance})",
  "change.componentFiles.one": "{count} file",
  "change.componentFiles.other": "{count} files",
  "change.noFindings.title": "No Findings",
  "change.noFindings.hint": "No conflict with an accepted decision or active constraint.",
  "change.uncertainty.title": "Nothing in Memory",
  "change.uncertainty.body": "Not an approval. Record a decision if this change should last.",
  "finding.verified": "verified from records",
  "finding.readFromDescription": "read from the description",
  "finding.meta": "{severity} · confidence {confidence}%",
  "finding.basedOn": "Based on:",
  "finding.confirm": "Confirm",
  "finding.falsePositive": "Mark as False Positive",
  "finding.acceptedException": "Accept as Exception",

  // ------------------------------------------------------------ components
  "component.kv.kind": "Kind",
  "component.kv.status": "Status",
  "component.kv.aliases": "Aliases",
  "component.kv.keys": "Keys",
  "component.kv.origin": "Origin",
  "component.locations.title": "Source Locations",
  "component.locations.description": "Paths that belong here. Rejected mappings aren’t inferred again.",
  "component.locations.anyRepository": "any repository",
  "component.locations.confirm": "Confirm",
  "component.locations.reject": "Reject",
  "component.locations.path": "Path",
  "component.locations.pathPlaceholder": "services/api/ or **/*.proto",
  "component.locations.repository": "Repository",
  "component.locations.map": "Map",
  "component.activeDecisions": "Active Decisions",
  "component.pendingDecisions": "Pending Decisions",
  "component.historicalDecisions": "History",
  "component.constraints": "Constraints",

  // ------------------------------------------------------------ onboarding
  "onboarding.description": "Push a snapshot, then confirm what to keep. Suggestions aren’t accepted automatically.",
  "onboarding.rejected": "Rejected. Won’t be suggested again.",
  "onboarding.created": "Created {id}.",
  "onboarding.open": "Open {id}",
  "onboarding.connect.title": "Connect a Repository",
  "onboarding.connect.intro": "Create an API key with evidence:record, then run this in the repository or in CI after each merge:",
  "onboarding.connect.infer": "Add --infer to suggest decisions from commits and pull requests (requires integration:manage).",
  "onboarding.empty.title": "No Snapshot",
  "onboarding.empty.hint": "Push one with the command above.",
  "onboarding.run.title": "Latest Run",
  "onboarding.run.status": "Status",
  "onboarding.run.revision": "Revision",
  "onboarding.run.finished": "Finished",
  "onboarding.run.documents": "Documents",
  "onboarding.run.documentsNone": "none",
  "onboarding.run.documentsItem": "{count} {kind}",
  "onboarding.run.history": "History",
  "onboarding.run.historyValue": "{commits} commits, {changes} pull requests",
  "onboarding.run.candidates": "Candidates",
  "onboarding.run.candidatesValue": "{decisions} decisions, {constraints} constraints, {components} components ({inMemory} already in memory, {suppressed} previously rejected)",
  "onboarding.run.inference": "Inference",
  "onboarding.run.inferenceValue": " {kept} kept, {dropped} dropped for missing citations",
  "onboarding.run.error": "Error",
  "onboarding.run.unresolved": "Unresolved",
  "onboarding.decisionCandidates": "Decision Candidates ({count})",
  "onboarding.constraintCandidates": "Constraint Candidates ({count})",
  "onboarding.constraintCandidates.description": "Found in agent instruction files.",
  "onboarding.componentCandidates": "Component Candidates ({count})",
  "onboarding.componentCandidates.description": "From package manifests and .diotia/components.yaml.",
  "onboarding.source.inferred": "inferred · {confidence}%",
  "onboarding.source.repositoryRecord": "repository record",
  "onboarding.source.declared": "declared",
  "onboarding.source.manifest": "manifest · {confidence}%",
  "onboarding.idConflict": "This decision_id is already used by a different decision in memory.",
  "onboarding.reject": "Reject",
  "onboarding.confirm": "Confirm",
  "onboarding.confirmAndActivate": "Confirm and Activate",

  // --------------------------------------------------------------- members
  "members.title": "Members",
  "members.invite": "Invite Member",
  "members.invite.title": "Invite Member",
  "members.invite.description": "Sends an email with a link to join.",
  "members.invite.emailPlaceholder": "teammate@company.com",
  "members.invite.role": "Role",
  "members.invite.roleHint": "Admins can manage members and API keys.",
  "members.invite.roleAria": "Role",
  "members.invite.send": "Send Invitation",
  "members.invite.failed": "Couldn’t send the invitation. Try again.",
  "members.invite.sent": "Invitation sent",
  "members.count.one": "{count} member",
  "members.count.other": "{count} members",
  "members.empty.title": "No Members",
  "members.pending.title": "Pending Invitations",
  "members.pending.description": "Invitations expire. Invite again to extend.",
  "members.pending.expires": "Expires {date}",

  // -------------------------------------------------------------- api keys
  "agents.title": "Coding Agents",
  "agents.description": "Claude Code, Codex, and other MCP clients you connect work here as you. They can propose decisions but never approve them.",
  "agents.connect.title": "Connect",
  "agents.connected.title": "Your Connected Agents",
  "agents.empty.title": "No Agents Connected",
  "agents.empty.hint": "Add the server above to your agent. It opens Diotia so you can choose a workspace.",
  "agents.connectedAt": "Connected {date}",
  "agents.disconnect": "Disconnect",
  "agents.disconnect.title": "Disconnect “{name}”?",
  "agents.disconnect.description": "It loses access immediately. To reconnect, start again from the agent.",
  "agents.disconnect.confirm": "Disconnect Agent",
  "agents.disconnected": "Agent disconnected",
  "oauthConsent.title": "Connect {client}",
  "oauthConsent.subtitle": "{client} will work in Diotia as you.",
  "oauthConsent.unnamed": "An agent",
  "oauthConsent.workspace": "Workspace",
  "oauthConsent.can": "Can read decisions and constraints, check plans, and propose decisions.",
  "oauthConsent.cannot": "Can’t approve, reject, or vote.",
  "oauthConsent.connect": "Connect",
  "oauthConsent.noWorkspace": "Create a workspace first.",
  "oauthConsent.invalidTitle": "Link Expired",
  "oauthConsent.invalid": "Start again from your agent.",
  "apiKeys.title": "API Keys",
  "apiKeys.create": "Create Key",
  "apiKeys.create.title": "Create API Key",
  "apiKeys.create.description": "Name it after the agent and machine. The key is shown only once.",
  "apiKeys.create.name": "Key Name",
  "apiKeys.create.namePlaceholder": "claude-code on laptop",
  "apiKeys.minted.title": "Copy This Key. It Won’t Be Shown Again.",
  "apiKeys.copyTooltip": "Copy to clipboard",
  "apiKeys.copyAria": "Copy Key",
  "apiKeys.copied": "Key copied",
  "apiKeys.clipboardUnavailable": "Clipboard unavailable",
  "apiKeys.clipboardHint": "Select the key and copy it.",
  "apiKeys.noneCreated": "No keys created.",
  "apiKeys.count.one": "{count} active key",
  "apiKeys.count.other": "{count} active keys",
  "apiKeys.empty.title": "No API Keys",
  "apiKeys.empty.hint": "Create a key for an agent to read this workspace.",
  "apiKeys.created": "Created {date}",
  "apiKeys.revoke": "Revoke",
  "apiKeys.revoke.title": "Revoke “{name}”?",
  "apiKeys.revoke.description": "Agents using this key lose access immediately. This can’t be undone.",
  "apiKeys.revoke.confirm": "Revoke Key",
  "apiKeys.revoked": "Key revoked",
  "connectors.title": "Connectors",
  "connectors.group.notInstalled": "Not Installed",
  "connectors.empty": "No connectors.",
  "connectors.status.enabled": "Enabled",
  "connectors.status.suspended": "Suspended",
  "connectors.status.requested": "Requested",
  "connectors.status.unavailable": "Unavailable",
  "connectors.installation": "Installation",
  "connectors.links.title": "Linked Accounts",
  "connectors.install": "Install",
  "connectors.installed": "{name} installed",
  "connectors.installFailed": "Couldn’t install {name}",
  "connectors.reason.denied": "Installation was canceled.",
  "connectors.reason.forbidden": "Only the owner or admin who started the install can finish it.",
  "connectors.reason.signed_out": "You weren’t signed in when you came back, so the install didn’t finish.",
  "connectors.reason.already_bound": "That account is already connected to another Diotia workspace.",
  "connectors.reason.failed": "Try again.",
  "connectors.notInstalled": "Not installed.",
  "connectors.notInstalled.notion": "Not installed. On Notion, pick a top-level page: everything under it is included.",
  "connectors.notConfigured": "Not available on this server.",
  "connectors.installedOn": "Installed {date}",
  "connectors.readOnly": "Only owners and admins can install or remove connectors.",
  "connectors.remove": "Remove",
  "connectors.remove.title": "Remove {name}?",
  "connectors.remove.description": "Diotia is also removed from {name}, and all linked accounts are unlinked.",
  "connectors.remove.confirm": "Remove {name}",
  "connectors.removed": "{name} removed",
  "connectors.links.empty": "No linked accounts. Members are linked by email the first time they talk to the agent.",
  "connectors.links.method.email": "Email",
  "connectors.links.method.manual": "Manual",
  "connectors.links.inactive": "Inactive",
  "connectors.links.left": "Left the Workspace",
  "connectors.links.unlink": "Unlink",
  "connectors.links.unlink.title": "Unlink {name}?",
  "connectors.links.unlinked": "Unlinked",
  "connectors.linked": "{name} account linked",
  "connectors.updated": "{name} settings updated",
  "connectors.requested": "Requested. An owner must approve on {name}.",
  "connectors.pending": "Requested. Waiting for an owner to approve on {name}.",
  "connectors.configure": "Configure on {name}",
  "connectors.resources.all.repository": "All repositories",
  "connectors.resources.all.page": "All pages",
  "connectors.resources.all.generic": "Everything",
  "connectors.resources.count.repository.one": "{count} repository",
  "connectors.resources.count.repository.other": "{count} repositories",
  "connectors.resources.count.page.one": "{count} page",
  "connectors.resources.count.page.other": "{count} pages",
  "connectors.resources.count.page.atLeast": "{count}+ pages",
  "connectors.resources.count.generic.one": "{count} item",
  "connectors.resources.count.generic.other": "{count} items",
  "connectors.resources.more": "+{count} more",
  "connectors.identify": "Link My {name} Account",
  "connectors.identify.label": "Your {name} Account",
  "connectors.identify.linkedAs": "Linked as {name}",
  "connectors.identify.unlinked": "Not linked.",
  "connectors.identifyFailed": "Couldn’t link {name}",
  "connectors.links.emptyIdentify": "No linked accounts.",
  "connectors.links.method.account": "Sign-in",
  "connectors.links.method.oauth": "Verified",
  "connectors.sync.title": "Find Decisions",
  "connectors.sync.now": "Explore Now",
  "connectors.sync.never": "Never explored",
  "connectors.sync.started": "Exploring {name}",
  "connectors.sync.failed": "Couldn’t explore {name}",
  "connectors.sync.status.queued": "Queued",
  "connectors.sync.status.reading": "Exploring",
  "connectors.sync.status.processing": "Processing",
  "connectors.sync.status.succeeded": "Completed",
  "connectors.sync.status.failed": "Failed",
  "connectors.sync.documents.one": "{count} page read",
  "connectors.sync.documents.other": "{count} pages read",
  "connectors.sync.coverage": "Opened {read} of {listed} found",
  "connectors.sync.candidates.one": "{count} candidate decision",
  "connectors.sync.candidates.other": "{count} candidate decisions",
  "connectors.sync.skipped.restricted": "{count} skipped (restricted)",
  "connectors.sync.skipped.overLimit": "{count} over the limit",
  "connectors.sync.skipped.unreadable": "{count} unreadable",
  "connect.summary": "Link {provider} account {name} ({team}) to {workspace}?",
  "connect.confirm": "Link Account",
  "connect.expiredTitle": "Link Expired",
  "connect.expired": "Ask the agent for a new link.",

  // ------------------------------------------------------------ operation
  "operation.title": "Task",
  "operation.back": "Overview",
  "operation.details": "Details",
  "operation.kv.type": "Type",
  "operation.kv.attempts": "Attempts",
  "operation.kv.attemptsValue": "{attempts} of {max}",
  "operation.kv.state": "State",
  "operation.state.terminal": "Finished",
  "operation.state.inProgress": "In progress",
  "operation.lastError": "Last Error",
  "operation.waiting": "Waiting for the task to finish…",

  // ------------------------------------------- data vocabulary: lifecycle
  "status.draft": "draft",
  "status.proposed": "proposed",
  "status.accepted": "accepted",
  "status.rejected": "rejected",
  "status.superseded": "superseded",
  "status.deprecated": "deprecated",
  "status.active": "active",
  "status.inactive": "inactive",
  "status.retired": "retired",
  "status.retracted": "retracted",
  "status.suggested": "suggested",
  "status.withdrawn": "withdrawn",
  "status.confirmed": "confirmed",
  "status.planned": "planned",
  "status.in_progress": "in progress",
  "status.merged": "merged",
  "status.reverted": "reverted",
  "status.abandoned": "abandoned",
  "status.queued": "queued",
  "status.running": "running",
  "status.pending": "pending",
  "status.succeeded": "completed",
  "status.failed": "failed",
  "status.dead": "stopped",
  "status.not_requested": "not requested",
  "status.not_configured": "not configured",

  // ----------------------------------------------- data vocabulary: roles
  "role.owner": "Owner",
  "role.admin": "Admin",
  "role.member": "Member",
  "actor.user": "user",
  "actor.api_key": "agent",
  "actor.service": "service",

  // ------------------------------------------ data vocabulary: provenance
  "provenance.imported": "imported",
  "provenance.inferred": "inferred",
  "provenance.human_confirmed": "human confirmed",
  "origin.authored": "authored",
  "origin.imported": "imported",
  "origin.inferred": "inferred",

  // ---------------------------------------- data vocabulary: component kind
  "componentKind.service": "service",
  "componentKind.module": "module",
  "componentKind.api": "API",
  "componentKind.datastore": "datastore",
  "componentKind.library": "library",
  "componentKind.workflow": "workflow",
  "componentKind.capability": "capability",

  // ------------------------------------- data vocabulary: decision history
  "event.created": "created",
  "event.edited": "edited",
  "event.transitioned": "status changed",
  "event.amendment_proposed": "amendment proposed",
  "event.amendment_accepted": "amendment accepted",
  "event.amendment_rejected": "amendment rejected",
  "event.amendment_withdrawn": "amendment withdrawn",
  "event.approval_recorded": "approval recorded",
  "event.retirement_proposed": "retirement proposed",
  "event.retirement_accepted": "retirement accepted",
  "event.retirement_declined": "retirement declined",
  "event.retirement_withdrawn": "retirement withdrawn",

  // ------------------------------------- data vocabulary: review semantics
  "findingClass.conformance": "conformance",
  "findingClass.conflict": "conflict",
  "findingClass.decision_gap": "decision gap",
  "findingClass.drift_candidate": "drift candidate",
  "findingClass.ambiguity": "ambiguity",
  "severity.low": "low",
  "severity.medium": "medium",
  "severity.high": "high",
  "severity.critical": "critical",
  "consequence.positive": "positive",
  "consequence.negative": "negative",
  "consequence.neutral": "neutral",

  // ------------------------------------ data vocabulary: constraint category
  "constraintCategory.dependency_boundary": "dependency boundary",
  "constraintCategory.security": "security",
  "constraintCategory.privacy": "privacy",
  "constraintCategory.consistency": "consistency",
  "constraintCategory.compatibility": "compatibility",
  "constraintCategory.performance": "performance",
  "constraintCategory.data_ownership": "data ownership",
  "constraintCategory.operational": "operational",
  "constraintCategory.product_invariant": "product invariant",
  "constraintCategory.migration": "migration",

  // --------------------------------------- data vocabulary: import actions
  "importAction.created": "created",
  "importAction.updated": "updated",
  "importAction.unchanged": "unchanged",
  "importAction.conflict": "conflict",
  "importAction.skipped": "skipped",
  "importAction.invalid": "invalid",

  // --------------------------------------------------------------- lineage
  "lineage.title": "Lineage",
  "lineage.mappedBy": "Matched {pattern} ({provenance})",
  "lineage.introducedBy": "Introduced by {decision}",
  "lineage.acceptedAt": "Accepted {date}",

  // ------------------------------------------------------------- proposals
  "change.significance.title": "Architecture Significance",
  "change.significance.worthy": "Likely decision-worthy",
  "change.significance.routine": "Routine",
  "change.significance.supersedes": "Would supersede: {ids}",
  "change.proposal.draft": "Draft Proposal",
  "change.proposal.drafted": "Drafted {id}. It stays a draft until accepted.",
  "change.proposal.exists": "Draft already exists: {id}.",
  "change.proposal.notNeeded": "No decision needed.",

  // ---------------------------------------------------------- architecture CI
  "ci.metrics.trust": "Review Outcomes",
  "ci.metrics.falsePositiveRate": "False-Positive Rate",
  "ci.metrics.acceptedFindingRate": "Acceptance Rate",
  "ci.metrics.unknown": "Not measured",
  "ci.drift.confirm": "Confirm",
  "ci.drift.dismiss": "Not Drift",
  "driftKind.repeated_exception": "repeated exception",
  "driftKind.unmapped_component": "unmapped component",
  "driftKind.unenforced_rule": "unenforced constraint",
  "driftKind.stale_decision": "stale decision",
  "driftKind.orphan_constraint": "orphan constraint",

  // ------------------------------------- data vocabulary: document kinds
  "documentKind.decision_record": "decision records",
  "documentKind.instruction_file": "instruction files",
  "documentKind.spec": "specs",
  "documentKind.manifest": "manifests",
  "documentKind.component_map": "component maps",

  // ========================================================== Get Started
  "getStarted.title": "Get Started",
  "getStarted.progress": "{done} of {total} done",
  "getStarted.source.title": "Add Decision Sources",
  "getStarted.source.body": "Connect Notion or Confluence, or upload documents.",
  "getStarted.source.running": "Reading your sources…",
  "getStarted.source.action": "Add Sources",
  "getStarted.decisions.title": "Confirm Decisions",
  "getStarted.decisions.body": "Decisions that were found take effect only once you confirm them.",
  "getStarted.decisions.action.drafts": "Open Drafts",
  "getStarted.decisions.action.reviews": "Open Reviews",
  "getStarted.agent.title": "Connect a Coding Agent",
  "getStarted.agent.body": "Claude Code and Codex check these decisions before they change code.",
  "getStarted.agent.action": "Connect an Agent",
  "getStarted.checks.title": "Turn On Pull Request Checks",
  "getStarted.checks.body": "Every pull request is checked against confirmed decisions.",
  "getStarted.checks.action": "Connect GitHub",

  // ================================================================ Inbox
  "inbox.title": "Inbox",
  "inbox.description": "Items waiting on you.",
  "inbox.empty.title": "Nothing Needs Your Attention",
  "inbox.empty.hint": "Conflicts on components you own, expiring exceptions, and questions for you show up here.",
  "inbox.empty.setup": "Pull request checks are off, so nothing can arrive here.",
  "inbox.conflict.title": "New Finding on {component}",
  "inbox.conflict.open": "Open Pull Request",
  "inbox.conflict.falsePositive": "Mark as False Positive",
  "inbox.conflict.where": "Resolve it in the pull request. It stays here until someone does.",
  "inbox.drift.title": "Drift Worth a Look",
  "inbox.review.title": "Waiting for Your Approval",
  "inbox.review.body": "{count} requests need your approval.",
  "inbox.review.action": "Go to Approvals",

  // ============================================================== Reviews
  "reviews.title": "Approvals",
  "reviews.description": "New decisions, amendments, and retirements waiting for approval.",
  "reviews.tab.mine": "For Me",
  "reviews.tab.submitted": "Submitted by Me",
  "reviews.empty.title": "No Requests",
  "reviews.empty.hint": "Proposed decisions, amendments, and retirements wait here for approval.",
  "reviews.empty.drafts": "Go to Drafts",
  "reviews.empty.submitted": "No Requests from You",
  "reviews.kind.decision": "new",
  "reviews.kind.amendment": "amend",
  "reviews.kind.retirement": "retire",
  "reviews.submitted": "Submitted by {actor} · {date}",
  "reviews.approvals": "{have} of {required} approvals",
  "reviews.cascade.warning": "Retires {count} constraints",
  "reviews.cascade.title": "What Happens If You Approve",
  "reviews.cascade.retires": "{id} will no longer be in effect",
  "reviews.cascade.replacedBy": "Replaced by {id}",
  "reviews.cascade.blocking": "{id} blocks merges now. After it’s retired, it won’t.",
  "reviews.cascade.none": "No constraint in effect is retired by approving this.",
  "reviews.approve.decision": "Approve",
  "reviews.approve.amendment": "Approve Amendment",
  "reviews.approve.retirement": "Approve Retirement",
  "reviews.reject": "Decline",
  "reviews.declined": "Declined.",
  "reviews.withdraw": "Withdraw",
  "reviews.withdrawn": "Withdrawn. Proposals go back to Drafts.",
  "reviews.voted": "You approved this.",
  "reviews.applied": "Approved and now in effect.",
  "reviews.pending": "Your approval is recorded: {have} of {required}.",
  "reviews.selfApproval": "You submitted this. The approval rules let you approve it yourself.",
  "reviews.selfApprovalBlocked": "You submitted this. The approval rules require someone else to approve it.",
  "reviews.amend.title": "What Changes",
  "reviews.amend.added": "added",
  "reviews.amend.note": "Accepted wording is never rewritten, only added to.",
  "reviews.create.rationale": "Rationale",
  "reviews.create.noRationale": "No rationale. The agent that drafted this left it empty rather than invent one. Add it before approving.",
  "reviews.retirement.reason": "Why Retire It",

  // =============================================================== Drafts
  "drafts.title": "Drafts",
  "drafts.description": "Facts an agent lined up. Rewrite them in your own words, then submit. Nobody is waiting.",
  "drafts.empty.title": "No Drafts",
  "drafts.empty.hint": "Candidates read from your repository and decisions drafted from pull requests wait here.",
  "drafts.fromRepository": "From Your Repository",
  "drafts.fromRepository.hint": "Quoted from files you already have. Needs your confirmation.",
  "drafts.fromCode": "From Code",
  "drafts.fromCode.hint": "Structure is there; the reason isn’t.",
  "drafts.fromPeople": "From Your Own Drafts",
  "drafts.fromPeople.hint": "Started by hand, not yet submitted.",
  "drafts.needs.reason": "Needs a reason",
  "drafts.needs.confirm": "Needs confirmation",
  "drafts.confirm": "Confirm",
  "drafts.reject": "Not a Decision",
  "drafts.edit": "Write It Up",
  "drafts.confidence": "{percent}% confidence",
  "drafts.quoted": "Quoted from {source}",
  "drafts.rationaleEmpty": "Rationale is empty on every drafted record. An agent states what it can prove and leaves the reason to you.",

  // =========================================================== My Changes
  "myChanges.title": "My Changes",
  "myChanges.description": "Whether your pull requests met the decisions already on record.",
  "myChanges.empty.title": "No Changes Checked",
  "myChanges.empty.hint": "Once checks are on, each pull request shows its result. This page shows the pattern over time.",
  "myChanges.empty.action": "Turn On Checks",
  "myChanges.badge.clean": "Meets {count} decisions",
  "myChanges.badge.noFindings": "No conflicts",
  "myChanges.badge.conflict": "{count} conflicts",
  "myChanges.pattern.title": "You Hit the Same Constraint {count} Times",
  "myChanges.pattern.body": "{id} — {statement}",
  "myChanges.pattern.question": "Do you keep forgetting, or is its scope too wide?",
  "myChanges.pattern.amend": "Propose an Amendment",

  // =============================================================== Health
  "health.title": "Health",
  "health.description": "Where memory may be out of date. An empty page is healthy.",
  "health.coverage.title": "Code Map Coverage",
  "health.coverage.claimed": "{percent}% of changed paths are mapped to a component",
  "health.coverage.unclaimed": "{count} unmapped",
  "health.coverage.ceiling": "This number caps what pull request checks and answers can cover. An unmapped path can’t be checked, and Ask Agent has nothing to say about it.",
  "health.coverage.action": "Map These Paths",
  "health.coverage.none": "No paths are mapped to a component.",
  "health.empty.title": "Nothing Out of Date",
  "health.empty.hint": "Stale decisions, orphan constraints, and repeatedly waived constraints show up once memory has run for a while.",
  "health.stale.title": "Stale",
  "health.orphan.title": "Orphan Constraints",
  "health.waived.title": "Repeatedly Waived",
  "health.unenforced.title": "Unenforced Constraints",
  "health.hypothesis": "Drift is a hypothesis to check. It never changes a decision on its own.",
  "health.confidence": "{percent}% confidence",

  // ============================================================= Settings
  "settings.title": "Settings",
  "settings.group.personal": "Personal",
  "settings.group.workspace": "Workspace",
  "settings.preferences.title": "Preferences",
  "settings.preferences.description": "Saved to your account, so they follow you to every device.",
  "settings.preferences.saved": "Preferences updated.",
  "settings.preferences.appearanceHint": "System follows your device’s light or dark setting.",
  "settings.profile.title": "Profile",
  "settings.profile.description": "How others see you in Diotia.",
  "settings.profile.avatar": "Profile Picture",
  "settings.profile.name": "Preferred Name",
  "settings.profile.username": "Username",
  "settings.profile.usernameTaken": "This username is taken.",
  "settings.profile.usernameInvalid": "Use 3–30 letters, numbers, underscores, or periods.",
  "settings.profile.saved": "Profile updated.",
  "settings.workspace.title": "General",
  "settings.workspace.description": "Name, URL slug, and avatar.",
  "settings.workspace.avatar": "Avatar",
  "avatar.upload": "Upload Image",
  "avatar.remove": "Remove",
  "avatar.unreadable": "Couldn’t read that image. Choose a PNG, JPEG, or WebP file.",
  "settings.workspace.save": "Save Changes",
  "settings.workspace.saved": "Workspace updated.",
  "settings.workspace.readOnly": "Only owners and admins can change these settings.",
  "settings.approval.title": "Approval Rules",
  "settings.approval.description": "How many approvals each kind of request needs before it takes effect.",
  "settings.approval.defaults": "These are the defaults and have never been changed.",
  "settings.approval.selfApprove": "Submitters Can Approve Their Own Requests",
  "settings.approval.create": "New Decisions",
  "settings.approval.amend": "Amendments",
  "settings.approval.retire": "Retirements",
  "settings.approval.people.one": "{count} person",
  "settings.approval.people.other": "{count} people",
  "settings.approval.saved": "Approval rules updated.",
  "settings.approval.fixed": "Fixed Rules",
  "settings.approval.agents": "Agents",
  "settings.approval.agentsValue": "Can draft and propose. Their approval never counts.",
  "settings.approval.blocking": "Blocking Merges",
  "settings.approval.blockingValue": "Only a person can make a constraint block merges, and only if it can be checked automatically.",
  "settings.repositories.title": "Repositories",
} as const;
